import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from './SearchableSelect';
import { referenceDataService } from '../services/referenceDataService';
import type { ReferenceDataDto } from '../types/salesOrder.types';

// FO-SOI-3: dropdown backed by the cached /city-towns or /state-provinces
// endpoints. Loads once on mount; consumers re-render via React state.

type Source = 'cityTowns' | 'stateProvinces' | 'terms' | 'schedules';

interface Props {
  source: Source;
  label: string;
  value: string | null;
  /** Emits the selected id and (for callers that persist a display name) its name. */
  onChange: (id: string, name?: string) => void;
  required?: boolean;
  disabled?: boolean;
}

// Module-scoped caches so repeated mounts don't re-hit the API.
const cache: Record<Source, ReferenceDataDto[] | null> = {
  cityTowns: null,
  stateProvinces: null,
  terms: null,
  schedules: null,
};

// 86d39ekrd — clear the module cache after a master-data sync so the next mount
// re-fetches the freshly-synced values instead of serving the stale snapshot
// (which otherwise persists until a full page reload). Pass a source to clear one,
// or omit to clear all.
export function resetReferenceDataCache(source?: Source) {
  if (source) {
    cache[source] = null;
    return;
  }
  (Object.keys(cache) as Source[]).forEach((k) => (cache[k] = null));
}

// 86d405z28 — resolve a reference id to its display name. The add-address dialog needs the
// order's State/Province and City/Town names for its context strip (it covers the two fields it
// derives from) and to seed City + the Province suggestion.
//
// ⚠️ The list is held in REACT STATE, never read straight off the module cache during render.
// Reading `cache[source]` in render is the documented React Compiler trap (86d39bx22 / 86d3d1z40):
// the compiler cannot see a module-level mutable object as a dependency, so it memoises the lookup
// on (source, id) and keeps serving the first result — `null` — no matter how many times the
// component re-renders afterwards. A forced re-render does NOT defeat it.
//
// That is not theoretical: it shipped, and the add-address dialog showed "State / Province — ·
// City / Town —", left City unprefilled, and — worst — made the Province (Y/N) suggestion fall
// through to YES for a Metro Manila order, because the name it tests was always null.
//
// `options` being state is what makes this correct; the module cache is now only a fetch-avoidance
// seed, never the render-time source of truth.
export function useReferenceName(source: Source, id: string | null | undefined): string | null {
  const [options, setOptions] = useState<ReferenceDataDto[]>(cache[source] ?? []);

  useEffect(() => {
    if (cache[source]) {
      setOptions(cache[source]!);
      return;
    }
    let cancelled = false;
    (source === 'cityTowns'
      ? referenceDataService.getCityTowns()
      : source === 'stateProvinces'
        ? referenceDataService.getStateProvinces()
        : source === 'terms'
          ? referenceDataService.getTerms()
          : referenceDataService.getSchedules()
    )
      .then((data) => {
        cache[source] = data;
        if (!cancelled) setOptions(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [source]);

  return useMemo(
    () => (id ? (options.find((o) => o.id === id)?.name ?? null) : null),
    [options, id],
  );
}

export function ReferenceDataCombobox({ source, label, value, onChange, required, disabled }: Props) {
  const [options, setOptions] = useState<ReferenceDataDto[]>(cache[source] ?? []);
  const [isLoading, setIsLoading] = useState(!cache[source]);

  useEffect(() => {
    if (cache[source]) return;
    let cancelled = false;
    setIsLoading(true);
    (source === 'cityTowns'
      ? referenceDataService.getCityTowns()
      : source === 'stateProvinces'
        ? referenceDataService.getStateProvinces()
        : source === 'terms'
          ? referenceDataService.getTerms()
          : referenceDataService.getSchedules()
    )
      .then((data) => {
        cache[source] = data;
        if (!cancelled) {
          setOptions(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return (
    <div className="space-y-1">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <SearchableSelect
        options={options}
        value={value}
        onSelect={(id) => onChange(id, options.find((o) => o.id === id)?.name)}
        placeholder={`Select ${label}…`}
        searchPlaceholder={`Search ${label.toLowerCase()}…`}
        disabled={disabled}
        loading={isLoading}
      />
    </div>
  );
}
